import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import {
  Sales, Tasks, Masters, Accounts, Files, Receipts, EarlyDelivery, apiErrorMessage,
} from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/auditFormat";
import { computeFinanceSummary, isV2Sale } from "../lib/finance";
import { TaskHoldControl, HoldDetailsCard } from "../components/TaskHoldControl";
import { TransitHoldBanner } from "../components/TransitHoldBadge";
import { ReleaseTransitButton } from "../components/ReleaseTransitButton";
import ActivityTimeline from "../components/ActivityTimeline";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Banknote, CheckCircle2, ShieldCheck, Wallet, Save, FileCheck,
  Upload, FileText, AlertTriangle, Eye, Trash2, Truck, Plus, Receipt as ReceiptIcon,
  User as UserIcon, Bike, Phone, MapPin, IdCard,
  CalendarClock, PieChart, ChevronRight,
} from "lucide-react";
import { cn } from "../lib/utils";
import { getUploadPolicy, validateUpload, downloadUrlFor } from "../lib/uploadPolicy";

const MODE_LABEL = {
  cash: "Cash", upi: "UPI",
  cheque: "Cheque", bank_transfer: "Bank Transfer",
};

const fmtCurrency = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function YesNoToggle({ value, onChange, readOnly, testid }) {
  const opt = (val, label) => (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => !readOnly && onChange(val)}
      data-testid={`${testid}-${val ? "yes" : "no"}`}
      className={cn(
        "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-all",
        readOnly && "cursor-not-allowed opacity-70",
        value === val
          ? val
            ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-200"
            : "border-rose-500 bg-rose-50 text-rose-800 ring-2 ring-rose-200"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-2" data-testid={testid}>
      {opt(true, "Yes")}{opt(false, "No")}
    </div>
  );
}

// ───────────────────────── Sale Context (compact) ─────────────────────────

function KV({ label, value, bold = false }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 py-1 last:border-b-0">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
        {label}
      </span>
      <span className={cn(
        "text-right text-xs truncate",
        bold ? "font-mono-data font-semibold text-slate-900" : "text-slate-800",
      )}>
        {value ?? <span className="text-slate-400">—</span>}
      </span>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
      <Icon className="h-3 w-3 text-primary" /> {children}
    </div>
  );
}

function EnhancedSaleContext({ sale, loc, financeName }) {
  const c = sale.customer || {};
  const v = sale.vehicle || {};
  const f = sale.finance || {};
  const d = sale.delivery || {};
  const fullAddress =
    [c.address, c.at_po, c.city, c.district, c.state].filter(Boolean).join(", ") || null;
  return (
    <Card className="border-slate-200 p-4" data-testid="acc-context-card">
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3">
        <section>
          <SectionTitle icon={UserIcon}>Customer</SectionTitle>
          <KV label="Name" value={c.name} bold />
          <KV label="Mobile" value={c.phone} bold />
          <KV label="Alt No." value={c.whatsapp} />
          <KV label="DOB" value={c.dob ? fmtDate(c.dob) : null} />
          <KV label="Aadhaar" value={formatAadhaar(c.aadhaar)} bold />
          <KV label="PAN" value={(c.pan || "").toUpperCase() || null} />
          <KV label="Email" value={c.email} />
          <KV label="PIN" value={c.pin_code} />
          {fullAddress && (
            <div className="mt-1 text-[11px] leading-snug text-slate-600" title={fullAddress}>
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Address · </span>
              {fullAddress}
            </div>
          )}
        </section>

        <section>
          <SectionTitle icon={Bike}>Vehicle</SectionTitle>
          <KV label="Model" value={v.model} bold />
          <KV label="VIN" value={v.vin} bold />
          <KV label="Engine" value={v.engine_no} />
          <KV label="Color" value={v.color} />
        </section>

        <section>
          <SectionTitle icon={ClipboardList}>Sale</SectionTitle>
          <KV label="SR No." value={sale.sr_number} bold />
          <KV label="Location" value={loc ? `${loc.name} (${loc.code})` : null} />
          <KV label="Promised" value={d.promised_date ? fmtDate(d.promised_date) : null} />
          <KV label="Buy Mode" value={
            f.buy_mode ? f.buy_mode.charAt(0).toUpperCase() + f.buy_mode.slice(1) : null
          } />
          <KV label="Finance Co." value={f.buy_mode === "finance" ? financeName : "—"} />
        </section>
      </div>
    </Card>
  );
}

// ───────────────────────── Price Break-up ─────────────────────────

// Component rows mirror the Sales Form → Payments Tab structure so Accounts
// reviews the same line items the DSE entered.
// v1 legacy layout rolled PP/AMC/RSA into a single "VAS" row. v2 (Feb-2026)
// spec calls for each service to be shown individually, matching the new
// price-breakup design across the app.
const PRICE_ROWS_V1 = [
  { key: "exshowroom",       label: "Ex-Showroom Price" },
  { key: "registration",     label: "Registration" },
  { key: "hypothecation",    label: "Hypothecation",       financeOnly: true },
  { key: "insurance",        label: "Insurance" },
  { key: "vas",              label: "VAS (PP + AMC + RSA)", composite: ["pp", "amc", "rsa"] },
  { key: "accessories",      label: "Accessories" },
  { key: "admin_incidental", label: "Admin & Incidental" },
];
const PRICE_ROWS_V2 = [
  { key: "exshowroom",       label: "Ex-Showroom Price" },
  { key: "registration",     label: "Registration" },
  { key: "hypothecation",    label: "Hypothecation",       financeOnly: true },
  { key: "insurance",        label: "Insurance" },
  { key: "pp",               label: "VAS · Protection Plus" },
  { key: "amc",              label: "VAS · AMC" },
  { key: "rsa",              label: "VAS · RSA" },
  { key: "admin_incidental", label: "Admin & Incidental" },
];

function applyDiscount(amount, disc) {
  if (!disc) return { final: amount, off: 0 };
  const val = Number(disc.value) || 0;
  if (val <= 0) return { final: amount, off: 0 };
  const mode = (disc.mode || "abs").toLowerCase();
  const off = mode === "pct" ? amount * (val / 100) : val;
  return { final: Math.max(amount - off, 0), off };
}

function PriceBreakup({ sale, summary }) {
  const p = sale.payment || {};
  const discounts = p.discounts || {};
  const isFinance = summary.isFinance;
  const isV2 = isV2Sale(sale);
  const v2 = useMemo(() => computeFinanceSummary(sale), [sale]);

  // Always show every component row (mirrors the Sales Form Payments Tab),
  // even when the value is ₹0 — Accounts needs the full ledger view.
  // Hypothecation row is hidden for non-finance sales.
  const priceRowsDef = isV2 ? PRICE_ROWS_V2 : PRICE_ROWS_V1;
  const rows = priceRowsDef
    .filter((r) => !(r.financeOnly && !isFinance))
    .map((r) => {
      const base = r.composite
        ? r.composite.reduce((s, k) => s + (Number(p[k]) || 0), 0)
        : (Number(p[r.key]) || 0);
      const { final, off } = applyDiscount(base, discounts[r.key]);
      return { ...r, base, off, final };
    });

  const grossTotal = rows.reduce((s, r) => s + r.base, 0);
  const componentDiscounts = rows.reduce((s, r) => s + r.off, 0);
  const netBeforeOverall = grossTotal - componentDiscounts;
  const overall = discounts.total || null;
  const overallOff = overall ? applyDiscount(netBeforeOverall, overall).off : 0;
  const totalDiscount = componentDiscounts + overallOff;
  const totalVehicle = Math.max(netBeforeOverall - overallOff, 0);
  const downPayment = Number(p.down_payment) || 0;
  const dpAccessories = Number(p.dp_accessories) || 0;
  const customerRequired = isFinance ? downPayment + dpAccessories : totalVehicle;
  const financeAmount = isFinance ? Math.max(totalVehicle - customerRequired, 0) : 0;

  if (grossTotal === 0 && !downPayment && !dpAccessories) {
    return (
      <Card className="border-slate-200 p-4 text-center text-xs text-slate-500"
            data-testid="acc-price-breakup-empty">
        Price break-up not captured on this sale yet. Ask the DSE to fill the Payment step.
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="acc-price-breakup-card">
      <div className="border-b border-border px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
        Price Break-up
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="acc-price-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            <tr className="text-left">
              <th className="px-4 py-2">Component</th>
              <th className="px-4 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.key} data-testid={`price-row-${r.key}`} className="h-9">
                <td className="px-4 py-1.5 text-slate-800">{r.label}</td>
                <td className="px-4 py-1.5 text-right font-mono-data font-medium text-slate-900">
                  {fmtCurrency(r.base)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Sub Total / Gross */}
            <tr className="border-t-2 border-slate-200">
              <td className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                {isV2 ? "Gross Amount" : "Sub Total"}
              </td>
              <td className="px-4 py-2 text-right font-mono-data text-sm font-semibold text-slate-900"
                  data-testid="price-sub-total">
                {fmtCurrency(isV2 ? v2.gross : grossTotal)}
              </td>
            </tr>
            {/* Accessories row for v2 sits between Gross and Net On-Road */}
            {isV2 && (
              <tr>
                <td className="px-4 py-1.5 text-slate-800">Accessories</td>
                <td className="px-4 py-1.5 text-right font-mono-data font-medium text-slate-900"
                    data-testid="price-accessories">
                  {fmtCurrency(v2.accessories)}
                </td>
              </tr>
            )}
            {/* Discount — v1 only; v2 has no overall discount concept */}
            {!isV2 && totalDiscount > 0 ? (
              <tr data-testid="price-discount-row">
                <td className="px-4 py-1.5 text-xs italic text-slate-500">Discount</td>
                <td className="px-4 py-1.5 text-right font-mono-data text-xs font-semibold text-rose-600">
                  (− {fmtCurrency(totalDiscount)})
                </td>
              </tr>
            ) : !isV2 ? (
              <tr data-testid="price-discount-row-zero">
                <td className="px-4 py-1.5 text-xs italic text-slate-400">Discount</td>
                <td className="px-4 py-1.5 text-right font-mono-data text-xs text-slate-400">
                  (− ₹0)
                </td>
              </tr>
            ) : null}
            {/* Total Vehicle Amount / Net On-Road Price — bold, blue accent, light bg */}
            <tr className="border-t-2 border-primary/30 bg-primary/5">
              <td className="px-4 py-3 text-sm font-bold uppercase tracking-wider text-primary">
                {isV2 ? "Net On-Road Price" : "Total Vehicle Amount"}
              </td>
              <td className="px-4 py-3 text-right font-mono-data text-lg font-bold text-primary"
                  data-testid="price-total-vehicle">
                {fmtCurrency(isV2 ? v2.netOnRoad : totalVehicle)}
              </td>
            </tr>
            {/* v2: Discount row between Net On-Road and DDA */}
            {isV2 && v2.discount > 0 && (
              <tr>
                <td className="px-4 py-2 text-slate-800">
                  Discount
                  {v2.discountConfig?.mode === "pct" && v2.discountConfig?.value
                    ? ` (${v2.discountConfig.value}%)` : ""}
                </td>
                <td className="px-4 py-2 text-right font-mono-data font-medium text-rose-700"
                    data-testid="price-discount">
                  − {fmtCurrency(v2.discount)}
                </td>
              </tr>
            )}
            {/* v2: DDA + Receivable, inline in the breakup for a compact view */}
            {isV2 && isFinance && (
              <tr>
                <td className="px-4 py-2 text-slate-800">Dealer Disbursement Amount (DDA)</td>
                <td className="px-4 py-2 text-right font-mono-data font-medium text-slate-900"
                    data-testid="price-dda">
                  {v2.dda > 0 ? `− ${fmtCurrency(v2.dda)}` : "—"}
                </td>
              </tr>
            )}
            {isV2 && (
              <tr className="border-t-2 border-emerald-300 bg-emerald-50">
                <td className="px-4 py-3 text-sm font-bold uppercase tracking-wider text-emerald-800">
                  Amount to Receive from Customer
                </td>
                <td className="px-4 py-3 text-right font-mono-data text-lg font-bold text-emerald-800"
                    data-testid="price-receivable">
                  {fmtCurrency(v2.receivable)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {/* Receivable from Customer — v1 legacy layout uses Down Payment + DP Accessories.
         v2 sales already include the receivable inline in the breakup above. */}
      {!isV2 && isFinance && (
        <div className="border-t border-border bg-sky-50/40 px-5 py-4"
             data-testid="acc-finance-receivable-block">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700">
            <Wallet className="h-3.5 w-3.5" /> Receivable from Customer
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Finance case · Customer pays Downpayment + Accessories; balance is funded by the bank.
          </p>
          <dl className="mt-3 grid grid-cols-1 gap-y-1.5 sm:grid-cols-2 sm:gap-x-6">
            <ReceivableRow label="Downpayment"
              amount={fmtCurrency(downPayment)} />
            <ReceivableRow label="Accessories (DP)"
              amount={fmtCurrency(dpAccessories)} />
            <ReceivableRow label="Finance Amount"
              amount={fmtCurrency(financeAmount)}
              hint="From Bank / Finance Co." muted />
            <ReceivableRow
              label="Total Receivable from Customer"
              amount={fmtCurrency(customerRequired)}
              highlight />
          </dl>
        </div>
      )}
    </Card>
  );
}

function ReceivableRow({ label, amount, hint, highlight, muted }) {
  return (
    <div className={cn(
      "flex items-center justify-between gap-3 rounded-md px-2.5 py-1.5",
      highlight && "bg-sky-100/70 ring-1 ring-sky-200",
    )}>
      <div className="min-w-0">
        <div className={cn(
          "text-xs",
          highlight ? "font-bold uppercase tracking-wider text-sky-800" : "font-semibold text-slate-700",
          muted && "text-slate-500",
        )}>
          {label}
        </div>
        {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
      </div>
      <div className={cn(
        "font-mono-data text-right",
        highlight ? "text-base font-bold text-sky-800" : "text-sm font-semibold text-slate-900",
        muted && "text-slate-500",
      )}>
        {amount}
      </div>
    </div>
  );
}

// ───────────────────────── Payment Summary cards ─────────────────────────

function PaymentSummary({ summary, onAddPayment, canAdd }) {
  const {
    totalVehicle, requiredFromCustomer, received, balance, financeAmount, isFinance,
  } = summary;
  return (
    <Card className="border-primary/15 bg-primary/5 p-5" data-testid="acc-payment-summary">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
          <Wallet className="h-4 w-4 text-primary" /> Payment Summary
        </div>
        {canAdd && (
          <Button size="sm" onClick={onAddPayment}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-payment-btn">
            <Plus className="h-4 w-4" /> Add Payment
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <SummaryTile label="Total Vehicle Amount" amount={totalVehicle} tone="slate" testid="sum-total-vehicle" />
        <SummaryTile label="Customer Amount Required" amount={requiredFromCustomer} tone="sky" testid="sum-required" />
        <SummaryTile label="Total Payment Received" amount={received} tone="emerald" testid="sum-received" />
        <SummaryTile label="Balance Pending" amount={balance}
                     tone={balance <= 0 ? "emerald" : "amber"} testid="sum-balance" />
        {isFinance && (
          <SummaryTile label="Finance Amount" amount={financeAmount} tone="primary" testid="sum-finance" />
        )}
      </div>
    </Card>
  );
}

function SummaryTile({ label, amount, tone, testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary border-primary/20",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-white text-slate-900 border-slate-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <div className={cn("rounded-lg border p-3", tones[tone])} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="mt-1.5 font-display text-xl font-semibold tracking-tight">{fmtCurrency(amount)}</div>
    </div>
  );
}

// ───────────────────────── Payment Details table ─────────────────────────

function PaymentDetailsTable({ rows, userMap }) {
  if (!rows.length) {
    return (
      <Card className="border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="acc-payments-empty">
        No payments recorded yet. Use <strong>+ Add Payment</strong> to log the first receipt.
      </Card>
    );
  }
  return (
    <Card className="border-slate-200" data-testid="acc-payments-card">
      <div className="border-b border-border px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
        Payment Details ({rows.length})
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="acc-payments-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            <tr className="text-left">
              <th className="px-5 py-2.5">Date</th>
              <th className="px-3 py-2.5">Mode</th>
              <th className="px-3 py-2.5 text-right">Amount</th>
              <th className="px-3 py-2.5">Reference</th>
              <th className="px-3 py-2.5">Remarks</th>
              <th className="px-3 py-2.5">Added By</th>
              <th className="px-3 py-2.5">Logged At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`acc-payment-row-${r.id}`}>
                <td className="px-5 py-2.5 font-mono-data text-xs text-slate-700">
                  {r.received_on ? fmtDate(r.received_on) : "—"}
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                    {MODE_LABEL[r.mode] || r.mode}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono-data font-semibold text-emerald-700">
                  {fmtCurrency(r.amount)}
                </td>
                <td className="px-3 py-2.5 font-mono-data text-xs text-slate-700">{r.receipt_no || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{r.notes || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  {userMap[r.created_by]?.name || "—"}
                </td>
                <td className="px-3 py-2.5 font-mono-data text-[11px] text-slate-500">
                  {r.created_at ? fmtDateTime(r.created_at) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ───────────────────────── Add Payment dialog ─────────────────────────

function AddPaymentDialog({ open, onClose, saleId, userId, balance, onSaved }) {
  const [mode, setMode] = useState("cash");
  const [amount, setAmount] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setMode("cash"); setAmount(""); setReceiptNo(""); setRemarks("");
      setReceivedOn(new Date().toISOString().slice(0, 10));
      setFile(null); setErr("");
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const amt = Number(amount);
    if (!amt || amt <= 0) return setErr("Enter a valid amount greater than 0");
    if (amt > balance + 0.01) return setErr(`Payment exceeds required amount (₹${balance.toLocaleString("en-IN")})`);
    setSubmitting(true);
    try {
      let receipt_file_id = null;
      if (file) {
        const f = await Files.upload(saleId, "payment_receipt", file, userId);
        receipt_file_id = f.id;
      }
      await Receipts.add(saleId, {
        mode, amount: amt,
        received_on: receivedOn,
        receipt_no: receiptNo || null,
        notes: remarks || null,
        receipt_file_id,
        user_id: userId,
      });
      toast.success(`Payment of ${fmtCurrency(amt)} recorded`);
      onSaved?.();
      onClose();
    } catch (e2) {
      setErr(apiErrorMessage(e2) || "Failed to save payment");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="add-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptIcon className="h-5 w-5 text-primary" />
            Add Payment Entry
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Payment Mode *</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="ap-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash" data-testid="ap-mode-cash">Cash</SelectItem>
                  <SelectItem value="upi" data-testid="ap-mode-upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer" data-testid="ap-mode-bank">Bank Transfer</SelectItem>
                  <SelectItem value="cheque" data-testid="ap-mode-cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount *</Label>
              <Input
                type="number" min="0" step="1" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                data-testid="ap-amount-input" autoFocus
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Received On</Label>
              <Input type="date" value={receivedOn}
                     onChange={(e) => setReceivedOn(e.target.value)}
                     data-testid="ap-received-on-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Reference Number{" "}
                <span className="text-[10px] font-normal text-slate-500">
                  (Txn ID / UPI Ref / Cheque No)
                </span>
              </Label>
              <Input value={receiptNo}
                     onChange={(e) => setReceiptNo(e.target.value)}
                     placeholder="optional"
                     data-testid="ap-reference-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Remarks</Label>
            <Textarea rows={2} value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Optional notes for the audit log"
                      data-testid="ap-remarks-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Upload Receipt (optional)</Label>
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:border-slate-400">
              <Upload className="h-4 w-4" />
              {file ? file.name : "Choose file (PDF / image)"}
              <input
                type="file" accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="ap-file-input"
              />
            </label>
          </div>

          {err && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700"
                 data-testid="ap-error">
              {err}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="ap-submit-btn">
              {submitting ? "Saving…" : "Save Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── Workbench (main) ─────────────────────────

export default function AccountsWorkspace() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations, users } = useApp();

  const [sale, setSale] = useState(null);
  const [task, setTask] = useState(null);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [sidebarFiles, setSidebarFiles] = useState({ aadhar: null, pan: null, vin_photo: null });
  const [financeMasters, setFinanceMasters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [auditRev, setAuditRev] = useState(0);
  const [edPendingCount, setEdPendingCount] = useState(0);

  // Verification form state
  const [paymentsVerified, setPaymentsVerified] = useState(null);
  const [doVerified, setDoVerified] = useState(null);
  const [disbursementVerified, setDisbursementVerified] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [billingLedger, setBillingLedger] = useState("");
  const [notes, setNotes] = useState("");
  const [vehicleInTransit, setVehicleInTransit] = useState(null);

  const load = useCallback(async () => {
    const s = await Sales.get(saleId);
    setSale(s);
    const tasks = await Tasks.list({ sale_id: saleId, type: "ACCOUNTS_INVOICE", with_sla: "true" });
    setTask(tasks[0] || null);
    const files = await Files.list(saleId, "tax_invoice");
    setInvoiceFiles(files);
    // Side-rail document quick-view: latest Aadhar / PAN / VIN photo.
    const allFiles = await Files.list(saleId);
    const latest = (type) => {
      const matches = (allFiles || []).filter((f) => f.type === type && !f.is_deleted);
      if (!matches.length) return null;
      return matches.sort((a, b) =>
        (b.uploaded_at || "").localeCompare(a.uploaded_at || ""),
      )[0];
    };
    setSidebarFiles({
      aadhar: latest("aadhar"),
      pan: latest("pan"),
      vin_photo: latest("vin_photo"),
    });
    const acc = s.accounts || {};
    setPaymentsVerified(acc.payments_verified ?? null);
    setDoVerified(acc.do_verified ?? null);
    setDisbursementVerified(acc.disbursement_verified ?? null);
    setInvoiceNumber(acc.invoice_number || "");
    setInvoiceDate(acc.invoice_date || "");
    setBillingLedger(acc.billing_ledger_name || "");
    setNotes(acc.notes || "");
    if (s.transit_hold?.active || s.transit_hold?.released_at) {
      setVehicleInTransit(!!s.transit_hold?.active);
    } else {
      setVehicleInTransit(null);
    }
    setAuditRev((n) => n + 1);
  }, [saleId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => setFinanceMasters(await Masters.list({ type: "finance_company" })))();
  }, []);

  // Early Delivery pending count for the top-right pill
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const d = await EarlyDelivery.queue({ status: "pending" });
        if (!cancelled) setEdPendingCount(d?.pending_count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Delete a receipt — uses Receipts.save() to replace the full list.
  const onDeleteReceipt = useCallback(async (receiptId) => {
    if (!sale) return;
    if (!window.confirm("Delete this payment receipt? This action cannot be undone.")) return;
    const next = (sale.payment_receipts || []).filter((r) => r.id !== receiptId);
    try {
      await Receipts.save(sale.id, next, activeUser?.id);
      toast.success("Receipt deleted");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to delete receipt");
    }
  }, [sale, activeUser, load]);

  const isCompleted = task?.status === "completed";
  const readOnly = (activeUser?.role !== "Accounts" && activeUser?.role !== "Admin") || isCompleted;
  // Payment-receipt edits are restricted to Accounts / Admin only. Sales
  // Manager can view but not add / delete receipts (spec addendum, Feb-2026).
  const RECEIPT_EDIT_ROLES = ["Accounts", "Admin"];
  const canEditReceipts =
    RECEIPT_EDIT_ROLES.includes(activeUser?.role) && !isCompleted;
  const isFinance = ((sale?.finance?.buy_mode || "cash").toLowerCase() === "finance");
  const financeName = useMemo(() =>
    financeMasters.find((f) => f.id === sale?.finance?.finance_company_id)?.name || "—",
    [financeMasters, sale]);
  const loc = locations.find((l) => l.id === sale?.location_id);
  const userMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users],
  );

  // Receipts data — pulled from sale.payment_receipts (same source as backend)
  const receiptRows = useMemo(
    () => [...(sale?.payment_receipts || [])].sort(
      (a, b) => (a.received_on || "").localeCompare(b.received_on || ""),
    ),
    [sale],
  );

  const summary = useMemo(() => computeSummary(sale), [sale]);

  const canComplete = useMemo(() => {
    if (!paymentsVerified) return false;
    if (isFinance && (!doVerified || !disbursementVerified)) return false;
    if (!invoiceNumber.trim()) return false;
    if (!billingLedger.trim()) return false;
    if (invoiceFiles.length === 0) return false;
    if (vehicleInTransit === null) return false;
    return true;
  }, [paymentsVerified, doVerified, disbursementVerified, invoiceNumber,
      billingLedger, invoiceFiles, isFinance, vehicleInTransit]);

  const onUploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateUpload("tax_invoice", file);
    if (err) { toast.error(err); e.target.value = ""; return; }
    setUploading(true);
    try {
      await Files.upload(saleId, "tax_invoice", file, activeUser?.id);
      toast.success("Tax invoice uploaded");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const onRemoveInvoice = async (fid) => {
    if (!window.confirm("Remove this invoice file?")) return;
    try {
      await Files.remove(fid);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    }
  };

  const onSaveDraft = async () => {
    setSaving(true);
    try {
      await Accounts.saveDraft(saleId, {
        payments_verified: paymentsVerified,
        do_verified: doVerified,
        disbursement_verified: disbursementVerified,
        invoice_number: invoiceNumber || null,
        invoice_date: invoiceDate || null,
        billing_ledger_name: billingLedger || null,
        notes: notes || null,
        user_id: activeUser?.id,
      });
      toast.success("Draft saved");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed to save draft");
    } finally { setSaving(false); }
  };

  const onComplete = async () => {
    if (vehicleInTransit === null) {
      toast.error("Please confirm whether the vehicle is in transit");
      return;
    }
    setCompleting(true);
    try {
      await Accounts.complete(saleId, {
        payments_verified: paymentsVerified,
        do_verified: doVerified,
        disbursement_verified: disbursementVerified,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate || null,
        billing_ledger_name: billingLedger,
        notes: notes || null,
        vehicle_in_transit: vehicleInTransit === true,
        user_id: activeUser?.id,
      });
      toast.success(
        vehicleInTransit
          ? "Invoice completed · Vehicle marked Transit Hold"
          : "Invoice completed · Back Office tasks created",
        { duration: 3000 },
      );
      navigate("/accounts");
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Validation failed");
    } finally { setCompleting(false); }
  };

  if (!sale) return <div className="py-10 text-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5 pb-24" data-testid="accounts-workspace-page">
      {/* ───── Top header ───── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/accounts")}
            className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
            data-testid="accounts-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Queue
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Accounts Workbench
            </h1>
            {isCompleted && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" /> Invoice Completed
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Financial checkpoint before Back Office. Verify payments, finance DO,
            disbursement and invoice processing.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/accounts")}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            data-testid="acc-ed-pill"
          >
            <CalendarClock className="h-4 w-4 text-slate-500" />
            Early Delivery Requests
            <span className={cn(
              "inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-bold",
              edPendingCount > 0 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600",
            )}>
              {edPendingCount}
            </span>
          </button>
        </div>
      </div>

      {/* ───── KPI strip ───── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AccountsKpi
          tone="blue"
          Icon={FileCheck}
          label="Total Vehicle Amount"
          value={fmtCurrency(summary.totalVehicle)}
          subtext="Vehicle price + charges"
          testid="kpi-total-vehicle"
        />
        <AccountsKpi
          tone="emerald"
          Icon={Wallet}
          label="Received Amount"
          value={fmtCurrency(summary.received)}
          subtext="Total payments received"
          testid="kpi-received"
        />
        <AccountsKpi
          tone="amber"
          Icon={ReceiptIcon}
          label="Balance Pending"
          value={fmtCurrency(summary.balance)}
          subtext="Amount pending to receive"
          highlight
          testid="kpi-balance"
        />
        <AccountsKpi
          tone="violet"
          Icon={PieChart}
          label="Payments Count"
          value={String(receiptRows.length)}
          subtext="Total receipts recorded"
          testid="kpi-payments-count"
        />
      </div>

      {/* ───── Main grid: left (70%) main content + right (30%) sticky sidebar ───── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-10">
        {/* LEFT column */}
        <div className="space-y-4 lg:col-span-7">
          <TransitHoldBanner sale={sale} action={
            (activeUser?.role === "DSE" || activeUser?.role === "Accounts" || activeUser?.role === "Admin") ? (
              <ReleaseTransitButton saleId={sale.id} userId={activeUser?.id}
                vinPresent={!!sale.vehicle?.vin} onDone={load} />
            ) : null
          } />
          <HoldDetailsCard task={task} />

          {/* Payments / Receipts */}
          <PaymentsReceiptsCard
            rows={receiptRows}
            userMap={userMap}
            canAdd={canEditReceipts && summary.balance > 0}
            onAddPayment={() => setAddPaymentOpen(true)}
            onDelete={canEditReceipts ? onDeleteReceipt : null}
          />

          {/* Price Break-up */}
          <PriceBreakup sale={sale} summary={summary} />

          {/* Verification (side-by-side) */}
          <Card className="border-slate-200 p-5 sm:p-6" data-testid="verification-section">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
              Verification
            </div>
            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">All payments &amp; amounts verified?</Label>
                <YesNoToggle value={paymentsVerified} onChange={setPaymentsVerified}
                  readOnly={readOnly} testid="toggle-payments" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  Vehicle in transit?
                  <Truck className="h-3.5 w-3.5 text-blue-500" />
                </Label>
                <YesNoToggle value={vehicleInTransit} onChange={setVehicleInTransit}
                  readOnly={readOnly} testid="toggle-transit" />
              </div>
            </div>
            {vehicleInTransit && !readOnly && (
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                On completion, this sale will be marked <strong>Transit Hold</strong>. Use
                "Release Transit Hold" once the vehicle arrives.
              </div>
            )}
            {isFinance && (
              <div className="mt-5 space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                  Finance Case — Extra checks
                </div>
                <Label className="text-sm font-semibold">Delivery Order (DO) verified?</Label>
                <YesNoToggle value={doVerified} onChange={setDoVerified}
                  readOnly={readOnly} testid="toggle-do" />
                <Label className="mt-3 block text-sm font-semibold">Disbursement amount verified?</Label>
                <YesNoToggle value={disbursementVerified} onChange={setDisbursementVerified}
                  readOnly={readOnly} testid="toggle-disbursement" />
              </div>
            )}
          </Card>

          {/* Invoice Details */}
          <Card className="border-slate-200 p-5 sm:p-6" data-testid="invoice-section">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Invoice Details</div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Invoice Number *</Label>
                <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
                  disabled={readOnly} placeholder="e.g. INV-2026-00421"
                  data-testid="invoice-number" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Invoice Date *</Label>
                <Input type="date" value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  disabled={readOnly} data-testid="invoice-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Billing Ledger Name *</Label>
                <Input value={billingLedger}
                  onChange={(e) => setBillingLedger(e.target.value)}
                  disabled={readOnly}
                  placeholder="e.g. ABC Corporation / HDFC Bank Ltd / Customer Name"
                  data-testid="billing-ledger-name-input" />
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <Label className="text-xs font-semibold">Upload Tax Invoice *</Label>
              <p className="text-[11px] font-medium text-amber-700">
                * Upload invoice with seal and signature.
              </p>
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
                {invoiceFiles.length === 0 ? (
                  <div className="text-center">
                    <FileText className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-2 text-xs text-slate-500">No invoice uploaded yet</p>
                    <p className="text-[10px] text-slate-400">Supported formats: PDF, JPG, PNG (Max. 10MB)</p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {invoiceFiles.map((f) => (
                      <li key={f.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 flex-shrink-0 text-slate-500" />
                          <span className="truncate text-sm text-slate-900">{f.original_filename || "invoice"}</span>
                          <span className="text-xs text-slate-400">
                            ({Math.round((f.size || 0) / 1024)} KB)
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <a href={Files.url(f.id)} target="_blank" rel="noreferrer"
                            className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"
                            data-testid={`invoice-preview-${f.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </a>
                          {!readOnly && (
                            <button onClick={() => onRemoveInvoice(f.id)}
                              className="rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50"
                              data-testid={`invoice-remove-${f.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {!readOnly && (
                  <div className="mt-3">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      <Upload className="h-4 w-4" />
                      {invoiceFiles.length ? "Replace file" : "Upload Invoice"}
                      <input type="file" accept="application/pdf,image/*"
                        onChange={onUploadInvoice} disabled={uploading} className="hidden"
                        data-testid="invoice-file-input" />
                    </label>
                    {uploading && <span className="ml-2 text-xs text-slate-500">Uploading…</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 space-y-1.5">
              <Label className="text-xs font-semibold">Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                disabled={readOnly} data-testid="accounts-notes" />
            </div>
          </Card>

          {isCompleted && sale.accounts?.verified_at && (
            <Card className="border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-900">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span>
                  Invoice <strong>{sale.accounts.invoice_number}</strong>
                  {sale.accounts.invoice_date && <> dated {sale.accounts.invoice_date}</>} verified on{" "}
                  {fmtDateTime(sale.accounts.verified_at)}.
                </span>
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT column — sticky */}
        <div className="lg:col-span-3">
          <RightSidebar
            sale={sale}
            loc={loc}
            financeName={financeName}
            isFinance={isFinance}
            task={task}
            dseName={userMap[sale.dse_id]?.name || "—"}
            sidebarFiles={sidebarFiles}
            onOpenFullDetails={() => navigate(`/sales/${sale.id}`)}
          />
        </div>
      </div>

      {/* ───── Sticky bottom action bar ───── */}
      {!readOnly && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-2.5 shadow-[0_-4px_14px_-6px_rgba(15,23,42,0.12)] backdrop-blur"
          data-testid="acc-action-bar"
        >
          <div className="mx-auto flex max-w-[1600px] flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
            {/* Left: status + last updated */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Status
                </span>
                <StatusPill status={task?.status} slaPaused={task?.sla_paused} />
              </div>
              <div className="hidden items-center gap-2 sm:flex">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Last Updated
                </span>
                <span className="font-mono-data text-slate-700">
                  {sale.updated_at ? fmtDateTime(sale.updated_at) : "—"}
                </span>
                {sale.accounts?.verified_by && userMap[sale.accounts.verified_by] && (
                  <span className="text-slate-500">by {userMap[sale.accounts.verified_by].name}</span>
                )}
              </div>
              {!canComplete && (
                <span className="flex items-center gap-1.5 text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Complete all required fields to unlock invoice completion.
                </span>
              )}
            </div>
            {/* Right: action buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={onSaveDraft} disabled={saving} className="gap-1.5"
                data-testid="accounts-save-draft-btn">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Draft"}
              </Button>
              <Button variant="outline"
                onClick={() => setAddPaymentOpen(true)}
                disabled={summary.balance <= 0}
                className="gap-1.5"
                data-testid="acc-action-add-payment">
                <Plus className="h-4 w-4" /> Add Payment
              </Button>
              <TaskHoldControl task={task} userId={activeUser?.id} onChanged={load} />
              <Button onClick={onComplete}
                disabled={!canComplete || completing || task?.sla_paused}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
                data-testid="accounts-mark-complete-btn">
                <FileCheck className="h-4 w-4" />
                {completing ? "Completing…" : "Complete Invoice"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddPaymentDialog
        open={addPaymentOpen}
        onClose={() => setAddPaymentOpen(false)}
        saleId={sale.id}
        userId={activeUser?.id}
        balance={summary.balance}
        onSaved={load}
      />
    </div>
  );
}

// ───────────────────────── Computations ─────────────────────────

function computeSummary(sale) {
  if (!sale) {
    return {
      totalVehicle: 0, requiredFromCustomer: 0, received: 0, balance: 0,
      financeAmount: 0, isFinance: false,
    };
  }
  const p = sale.payment || {};
  const f = sale.finance || {};
  const isFinance = ((f.buy_mode || "cash").toLowerCase() === "finance");
  const isV2 = isV2Sale(sale);

  if (isV2) {
    // v2 pricing model — delegate to the shared helper so this component
    // matches every other price surface in the app.
    const s = computeFinanceSummary(sale);
    const received = (sale.payment_receipts || [])
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const balance = Math.max(s.receivable - received, 0);
    return {
      totalVehicle: s.netOnRoad,
      requiredFromCustomer: s.receivable,
      received, balance,
      financeAmount: s.dda,
      isFinance,
    };
  }

  // v1 legacy path
  const keys = ["exshowroom", "registration", "hypothecation", "insurance",
                "pp", "amc", "rsa", "accessories", "admin_incidental"];
  const totalVehicle = keys.reduce((s, k) => s + (Number(p[k]) || 0), 0);

  let requiredFromCustomer;
  let financeAmount = 0;
  if (isFinance) {
    requiredFromCustomer =
      (Number(p.down_payment) || 0) + (Number(p.dp_accessories) || 0);
    financeAmount = Math.max(totalVehicle - requiredFromCustomer, 0);
  } else {
    requiredFromCustomer = totalVehicle;
  }
  const received = (sale.payment_receipts || [])
    .reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const balance = Math.max(requiredFromCustomer - received, 0);
  return {
    totalVehicle, requiredFromCustomer, received, balance, financeAmount, isFinance,
  };
}

// ───────────────────────── New design-system pieces ─────────────────────────

function AccountsKpi({ tone, Icon, label, value, subtext, highlight, testid }) {
  const tones = {
    blue:    { card: "bg-blue-50/60 ring-blue-100",     icon: "bg-blue-100 text-blue-700",       value: "text-slate-900" },
    emerald: { card: "bg-emerald-50/60 ring-emerald-100", icon: "bg-emerald-100 text-emerald-700", value: "text-slate-900" },
    amber:   { card: "bg-amber-50/60 ring-amber-100",   icon: "bg-amber-100 text-amber-700",     value: "text-amber-700" },
    violet:  { card: "bg-violet-50/60 ring-violet-100", icon: "bg-violet-100 text-violet-700",   value: "text-slate-900" },
  }[tone] || {};
  return (
    <div
      className={cn(
        "rounded-xl p-4 ring-1 transition-shadow hover:shadow-sm",
        tones.card,
      )}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <span className={cn(
          "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl",
          tones.icon,
        )}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">
            {label}
          </div>
          <div className={cn(
            "mt-1 font-display font-bold leading-none tracking-tight",
            highlight ? "text-3xl" : "text-3xl",
            tones.value,
          )}>
            {value}
          </div>
          <div className="mt-1.5 text-xs text-slate-500">{subtext}</div>
        </div>
      </div>
    </div>
  );
}

function PaymentsReceiptsCard({ rows, userMap, canAdd, onAddPayment, onDelete }) {
  return (
    <Card className="border-slate-200" data-testid="acc-payments-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ReceiptIcon className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-bold text-slate-900">Payments / Receipts</div>
            <div className="text-[11px] text-slate-500">All recorded receipts against this sale</div>
          </div>
        </div>
        {canAdd && (
          <Button size="sm" onClick={onAddPayment}
            className="gap-1.5 bg-primary hover:bg-primary/90"
            data-testid="add-payment-btn">
            <Plus className="h-4 w-4" /> Add Payment
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          No payments recorded yet. Use <strong>+ Add Payment</strong> to log the first receipt.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="acc-payments-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <tr className="text-left">
                  <th className="px-5 py-2.5">Receipt No.</th>
                  <th className="px-3 py-2.5">Mode</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5">Received On</th>
                  <th className="px-3 py-2.5">Remarks</th>
                  <th className="px-3 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`acc-payment-row-${r.id}`} className="hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-mono-data text-xs font-semibold text-slate-900">
                      {r.receipt_no || `PAY-${(r.id || "").slice(-6)}`}
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                        {MODE_LABEL[r.mode] || r.mode}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono-data font-bold text-slate-900">
                      {fmtCurrency(r.amount)}
                    </td>
                    <td className="px-3 py-3 font-mono-data text-xs text-slate-700">
                      {r.received_on ? `${fmtDate(r.received_on)}  ${
                        r.received_on.length > 10 ? new Date(r.received_on).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""
                      }` : "—"}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{r.notes || "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
                          title="View details"
                          data-testid={`acc-payment-view-${r.id}`}
                          onClick={() => toast.message(
                            `Receipt ${r.receipt_no || r.id}`,
                            {
                              description: `${MODE_LABEL[r.mode] || r.mode} · ${fmtCurrency(r.amount)} on ${fmtDate(r.received_on)}${r.notes ? ` — ${r.notes}` : ""}${userMap?.[r.created_by]?.name ? ` · by ${userMap[r.created_by].name}` : ""}`,
                            },
                          )}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {onDelete && (
                          <button
                            type="button"
                            className="rounded-md border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50"
                            title="Delete receipt"
                            onClick={() => onDelete(r.id)}
                            data-testid={`acc-payment-delete-${r.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-5 py-2.5 text-[11px] text-slate-500">
            Showing 1 to {rows.length} of {rows.length} entries
          </div>
        </>
      )}
    </Card>
  );
}

function RightSidebar({ sale, loc, financeName, isFinance, dseName, sidebarFiles, onOpenFullDetails }) {
  const c = sale.customer || {};
  const v = sale.vehicle || {};
  const f = sale.finance || {};
  const d = sale.delivery || {};
  const ed = sale.early_delivery || {};
  const fullAddress = [c.address, c.at_po, c.city, c.district, c.state]
    .filter(Boolean).join(", ") || null;

  const deliveryLabel = sale.status === "delivered"
    ? (ed.delivery_type === "conditional" ? "Delivered · Early" : "Delivered")
    : (sale.status === "ready_for_delivery" ? "Ready" : "Pending");
  const deliveryTone = sale.status === "delivered"
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : (sale.status === "ready_for_delivery"
        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
        : "bg-amber-50 text-amber-700 ring-amber-200");

  const edLabel = ed.status === "pending" ? "Requested"
    : ed.status === "approved" ? "Approved"
    : ed.status === "rejected" ? "Rejected"
    : null;

  return (
    <div className="space-y-4 lg:sticky lg:top-4">
      {/* Customer Details */}
      <Card className="border-slate-200 p-4" data-testid="sidebar-customer">
        <SidebarHeader Icon={UserIcon} label="Customer Details" />
        <dl className="mt-3 space-y-2">
          <SidebarRow label="Customer Name" value={c.name} bold />
          <SidebarRow
            label="Mobile"
            value={c.phone}
            mono
            action={c.phone ? (
              <a href={`tel:${c.phone}`}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                title="Call customer"
                data-testid="sidebar-call-customer">
                <Phone className="h-3 w-3" />
              </a>
            ) : null}
          />
          <SidebarRow label="Alt Mobile" value={c.whatsapp} mono />
          <SidebarRow label="Address" value={fullAddress} multiline />
          <SidebarRow
            label="Aadhaar No."
            value={formatAadhaar(c.aadhaar)}
            mono
            action={sidebarFiles?.aadhar ? (
              <a
                href={Files.url(sidebarFiles.aadhar.id)}
                target="_blank"
                rel="noreferrer"
                className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50"
                title="Open Aadhaar Card"
                data-testid="sidebar-view-aadhar"
              >
                <Eye className="h-2.5 w-2.5" /> View
              </a>
            ) : null}
          />
          <SidebarRow
            label="PAN No."
            value={(c.pan || "").toUpperCase() || null}
            mono
            action={sidebarFiles?.pan ? (
              <a
                href={Files.url(sidebarFiles.pan.id)}
                target="_blank"
                rel="noreferrer"
                className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50"
                title="Open PAN Card"
                data-testid="sidebar-view-pan"
              >
                <Eye className="h-2.5 w-2.5" /> View
              </a>
            ) : null}
          />
        </dl>
        <button
          type="button"
          onClick={onOpenFullDetails}
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10"
          data-testid="sidebar-view-full-details"
        >
          See Full Details · Sale Detail Page <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </Card>

      {/* Vehicle Details */}
      <Card className="border-slate-200 p-4" data-testid="sidebar-vehicle">
        <SidebarHeader Icon={Bike} label="Vehicle Details" />
        <dl className="mt-3 space-y-2">
          <SidebarRow label="Model" value={v.model} bold />
          <SidebarRow
            label="VIN"
            value={v.vin}
            mono
            action={sidebarFiles?.vin_photo ? (
              <a
                href={downloadUrlFor("vin_photo", sidebarFiles.vin_photo, Files.url(sidebarFiles.vin_photo.id))}
                target="_blank"
                rel="noreferrer"
                className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50"
                title={(sidebarFiles.vin_photo.content_type || "").startsWith("image/") ? "Download VIN Photo (PDF)" : "Open VIN Photo"}
                data-testid="sidebar-view-vin-photo"
              >
                <Eye className="h-2.5 w-2.5" /> {(sidebarFiles.vin_photo.content_type || "").startsWith("image/") ? "PDF" : "View"}
              </a>
            ) : null}
          />
          <SidebarRow label="Engine No." value={v.engine_no} mono />
          <SidebarRow label="Color" value={v.color} />
          <SidebarRow
            label="Buy Mode"
            value={
              <span className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1",
                isFinance
                  ? "bg-amber-50 text-amber-800 ring-amber-200"
                  : "bg-slate-50 text-slate-700 ring-slate-200",
              )}>
                {isFinance ? <Wallet className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}
                {isFinance ? "Finance" : "Cash"}
              </span>
            }
          />
          {isFinance && (
            <SidebarRow label="Finance Co." value={financeName} />
          )}
          <SidebarRow
            label="Location"
            value={loc ? (
              <span className="inline-flex items-center gap-1 text-xs">
                <MapPin className="h-3 w-3 text-slate-400" />
                {loc.name} ({loc.code})
              </span>
            ) : null}
          />
          <SidebarRow
            label="Promised Delivery"
            value={d.promised_date ? fmtDate(d.promised_date) : null}
            mono
          />
        </dl>
      </Card>

      {/* Sale Details */}
      <Card className="border-slate-200 p-4" data-testid="sidebar-sale">
        <SidebarHeader Icon={IdCard} label="Sale Details" />
        <dl className="mt-3 space-y-2">
          <SidebarRow label="SR Number" value={sale.sr_number} mono bold />
          <SidebarRow label="Sale Date" value={sale.submitted_at ? fmtDate(sale.submitted_at) : (sale.created_at ? fmtDate(sale.created_at) : null)} mono />
          <SidebarRow label="Assigned DSE" value={dseName || "—"} />
          <SidebarRow label="Finance Status"
            value={isFinance ? (sale.accounts?.do_verified === true ? "DO Verified" : "—") : "—"} />
          <SidebarRow label="Delivery Status"
            value={
              <span className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1",
                deliveryTone,
              )}>
                {deliveryLabel}
              </span>
            }
          />
          {edLabel && (
            <SidebarRow label="Early Delivery"
              value={
                <span className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1",
                  ed.status === "approved" ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  : ed.status === "rejected" ? "bg-rose-50 text-rose-700 ring-rose-200"
                  : "bg-amber-50 text-amber-700 ring-amber-200",
                )}>
                  {edLabel}
                </span>
              }
            />
          )}
        </dl>
      </Card>
    </div>
  );
}

function SidebarHeader({ Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 border-b border-slate-100 pb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
      <Icon className="h-3.5 w-3.5 text-primary" />
      {label}
    </div>
  );
}

function SidebarRow({ label, value, bold, mono, multiline, action }) {
  return (
    <div className={cn("flex gap-3", multiline ? "items-start" : "items-center")}>
      <dt className="w-[42%] flex-shrink-0 text-[11px] font-medium text-slate-500">{label}</dt>
      <dd className={cn(
        "flex-1 min-w-0 text-[13px]",
        bold ? "font-semibold text-slate-900" : "text-slate-800",
        mono && "font-mono-data",
        !value && "italic text-slate-400",
        multiline ? "whitespace-normal break-words" : "truncate",
      )}>
        <span className="inline-flex items-center gap-1">
          {value || "—"}
          {action}
        </span>
      </dd>
    </div>
  );
}

function StatusPill({ status, slaPaused }) {
  const meta = slaPaused
    ? { label: "On Hold", cls: "bg-indigo-50 text-indigo-700 ring-indigo-200", dot: "bg-indigo-500" }
    : status === "completed"
    ? { label: "Invoice Completed", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500" }
    : { label: "Pending Verification", cls: "bg-amber-50 text-amber-700 ring-amber-200", dot: "bg-amber-500" };
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold ring-1",
      meta.cls,
    )}
    data-testid="acc-status-pill">
      <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
      {meta.label}
    </span>
  );
}

function formatAadhaar(v) {
  const clean = String(v || "").replace(/\D/g, "").slice(0, 12);
  if (!clean) return null;
  return clean.replace(/(\d{4})(?=\d)/g, "$1-");
}
